import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { AlertTriangle, Loader2, Repeat, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { PortalShell } from '@/components/portal/PortalShell';
import { PortalStatusPill } from '@/components/portal/PortalStatusPill';
import {
  useManageRecurringPlans,
  usePortalRecurringPlans,
  type PortalRecurringPlan,
} from '@/hooks/portal/usePortalRecurringPlans';
import { recurrenceSummary } from '@/lib/jobs/recurrenceSummary';
import { formatPlanPrice, recurringPlanStatus } from '@/lib/portal/recurringPlan';
import { portalVisitWhen } from '@/lib/portal/visitDisplay';
import { hasLivePortalSession } from '@/stores/portalAuth.store';

export const Route = createFileRoute('/(portal)/portal/plans/')({
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalRecurringPlansPage,
});

/** A payment the plan is waiting on: the open invoice settles it. */
const AWAITING_PAYMENT = new Set(['PAST_DUE', 'SUSPENDED']);

function PortalRecurringPlansPage() {
  const plans = usePortalRecurringPlans();
  const manage = useManageRecurringPlans();
  const live = plans.data?.plans.filter((plan) => plan.status !== 'ENDED') ?? [];
  const ended = plans.data?.plans.filter((plan) => plan.status === 'ENDED') ?? [];

  function onManage() {
    manage.mutate(undefined, {
      onError: () => toast.error("We couldn't open your plans. Please try again."),
    });
  }

  return (
    <PortalShell>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Recurring services</h1>
          <p className="text-sm text-muted-foreground">
            Your service plans: what each costs and when we come next.
          </p>
        </div>
        {/* Skip, pause or cancel, auto-renew and the card on file live on the
            self-service page, opened with a link made for this customer. */}
        {live.length > 0 ? (
          <Button size="sm" onClick={onManage} disabled={manage.isPending}>
            {manage.isPending ? (
              <Loader2 className="mr-1.5 size-4 animate-spin" />
            ) : (
              <Settings2 className="mr-1.5 size-4" />
            )}
            Manage plans
          </Button>
        ) : null}
      </div>

      {plans.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : plans.isError ? (
        <Card>
          <CardContent className="space-y-3 py-6 text-sm text-muted-foreground">
            <p>We couldn&apos;t load your recurring services. Please try again.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void plans.refetch()}
              disabled={plans.isFetching}
            >
              {plans.isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : live.length === 0 && ended.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            You don&apos;t have any recurring services.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {live.length > 0 ? (
            <div className="space-y-3">
              {live.map((plan) => (
                <PlanCard key={plan.jobId} plan={plan} />
              ))}
            </div>
          ) : null}
          {ended.length > 0 ? (
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Ended
              </p>
              {ended.map((plan) => (
                <PlanCard key={plan.jobId} plan={plan} />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </PortalShell>
  );
}

function PlanCard({ plan }: { plan: PortalRecurringPlan }) {
  const status = recurringPlanStatus(plan.status);
  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-portal-accent-surface text-portal-accent">
            <Repeat className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate text-sm font-semibold">{plan.name}</p>
              <PortalStatusPill label={status.label} tone={status.tone} />
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {recurrenceSummary(plan.cadence)}
              {plan.price ? ` · ${formatPlanPrice(plan.price)}` : ''}
            </p>
            {plan.nextVisit ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Next visit: {portalVisitWhen(plan.nextVisit)}
              </p>
            ) : null}
          </div>
        </div>
        {AWAITING_PAYMENT.has(plan.status) ? (
          <div className="flex items-start gap-2 rounded-md bg-portal-accent-surface p-2.5 text-xs text-portal-accent">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <p>
              Your last payment for this plan didn&apos;t go through.{' '}
              <Link to="/portal/invoices" className="font-medium underline underline-offset-2">
                Pay the open invoice
              </Link>{' '}
              to keep your service running.
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
